/**
 * miyakoFlightService.ts — 宮古空港のフライト情報（2026-09-01）
 *
 * 那覇空港は FIS の JSON を公開しているが、宮古空港には JSON の口が無い。
 * トップページ https://miyakoap.co.jp/ の「本日のフライト」に出発・到着の
 * 両方が HTML で埋め込まれているので、サーバ側（sys/api/miyako_flights.php）で
 * パースして JSON にしたものをここで読む。
 *   ※ /flights/ は出発便しか載っていない。到着はトップページにしか無い。
 *
 * 便名の照合ロジック（parseReservationFlight / findArrival）と表示ヘルパーは
 * 那覇と共通なので nahaFlightService のものをそのまま使う。この層は
 * **宮古の生データを NahaArrival の形に揃えるだけ**。
 *
 * 那覇との差:
 *   - 実績時刻(actual)が無い。変更時刻(estimated)と状態文言で表す
 *   - コードシェアの展開が無い（予約に「JAL◯◯」と書かれていて実運航が JTA の
 *     ような場合、那覇より照合が弱い）
 *   - 代わりに **遅延理由(remark)** が取れる。那覇の FIS には無い情報
 */
import { SYS_API_BASE } from '../constants/calendar';
import { STATIC_AIRLINE_ALIASES } from './nahaFlightService';
import type { NahaArrival, NahaFlightData } from './nahaFlightService';

interface MiyakoFlightRow {
  carrier: string;       // IATA 2レター（サーバ側で ANA→NH 等に変換済み）
  carrier_code: string;  // 宮古空港サイトの3レター（ANA / JTA / JAL …）
  flight_no: number;
  origin?: string;
  destination?: string;
  scheduled: string | null;
  estimated: string | null;
  status: string | null;
  remark: string | null;
}

interface MiyakoFlightPayload {
  ok: boolean;
  fetched_at: string;
  source_updated_at: string | null;
  arrivals: MiyakoFlightRow[];
  departures: MiyakoFlightRow[];
  error?: string;
}

const toArrival = (row: MiyakoFlightRow, dateYmd: string): NahaArrival => ({
  carrier: String(row.carrier || '').toUpperCase(),
  flightNo: Number(row.flight_no),
  originJp: String(row.origin || row.destination || '').replace(/[\s　]/g, ''),
  originEn: '',
  scheduled: row.scheduled,
  estimated: row.estimated,
  actual: null,          // 宮古は実績時刻を別に持たない
  statusJp: row.status || '',
  statusEn: '',
  isInternational: false,
  codeshares: [],        // 宮古空港サイトはコードシェアを出していない
  dateYmd,
  remark: row.remark,
});

/**
 * 宮古空港の到着便を取得する。返り値の形は那覇と同じなので、
 * daylist 側の照合・表示コードはそのまま使える。
 *
 * 宮古空港サイトは「本日ぶん」しか出していないため、targetYmd は
 * 付与する日付としてのみ使う（過去日・未来日は取得できない）。
 */
export async function fetchMiyakoFlights(targetYmd: string): Promise<NahaFlightData> {
  const res = await fetch(`${SYS_API_BASE}/miyako_flights.php`);
  if (!res.ok) throw new Error(`フライト情報の取得に失敗しました (HTTP ${res.status})`);
  const json: MiyakoFlightPayload = await res.json();

  // パースが壊れた（サイト改修等）ときは黙って空を返さず、はっきり失敗させる
  if (!json.ok) {
    throw new Error(json.error || '宮古空港のフライト情報を取得できませんでした');
  }

  const normKey = (v: string) => String(v).normalize('NFKC').replace(/[\s　]/g, '').toUpperCase();
  const airlineAliasMap: Record<string, string> = {};
  Object.entries(STATIC_AIRLINE_ALIASES).forEach(([k, v]) => { airlineAliasMap[normKey(k)] = v; });

  return {
    fetchedAt: json.fetched_at,
    arrivals: (json.arrivals || []).map(row => toArrival(row, targetYmd)),
    airlineAliasMap,
  };
}

/**
 * 宮古空港の出発便。返却のお客様の搭乗便が遅れていないかの確認に使える。
 * 形は到着と同じ（originJp に行き先が入る）。
 */
export async function fetchMiyakoDepartures(targetYmd: string): Promise<NahaArrival[]> {
  const res = await fetch(`${SYS_API_BASE}/miyako_flights.php`);
  if (!res.ok) throw new Error(`フライト情報の取得に失敗しました (HTTP ${res.status})`);
  const json: MiyakoFlightPayload = await res.json();
  if (!json.ok) throw new Error(json.error || '宮古空港のフライト情報を取得できませんでした');
  return (json.departures || []).map(row => toArrival(row, targetYmd));
}
